import React from "react";
import ControlledTableContext from "./ControlledTableContext";

const LEFT_ELLIPSIS = "LEFT";
const RIGHT_ELLIPSIS = "RIGHT";

export const ControlledPagination = ({
  totalRecords,
  pageSize = 10,
  pageNeighbours = 0,
  gotoPage,
  canPreviousPage,
  canNextPage,
  setStart,
  setEnd
}) => {
  const { currentPage, setCurrentPage } = React.useContext(ControlledTableContext);
  const totalPages = Math.ceil(totalRecords / pageSize);

  /**
   * Helper method for creating a range of numbers
   * range(1, 5) => [1, 2, 3, 4, 5]
   */
  const range = (from, to, step = 1) => {
    let i = from;
    const range = [];

    while (i <= to) {
      range.push(i);
      i += step;
    }

    return range;
  };

  /**
   * Let's say we have 10 pages and we set pageNeighbours to 2
   * Given that the current page is 6
   * The pagination control will look like the following:
   *
   * (1) ... {4 5} [6] {7 8} ... (10)
   *
   * (x) => terminal pages: first and last page(always visible)
   * [x] => represents current page
   * {...x} => represents page neighbours
   */
  const fetchPageNumbers = () => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(...) and right(...) ellipsis
     */
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);
      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        // handle: (1) ... {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_ELLIPSIS, ...extraPages, ...pages];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} ... (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, RIGHT_ELLIPSIS];
          break;
        }

        // handle: (1) ... {4 5} [6] {7 8} ... (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_ELLIPSIS, ...pages, RIGHT_ELLIPSIS];
          break;
        }
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  const goToPage = (page: number) => {
    const currentPage = Math.max(0, Math.min(page, totalPages));
    const end = totalPages != currentPage ? currentPage * pageSize : totalRecords;
    gotoPage(page - 1);
    setCurrentPage(currentPage);
    setStart(currentPage * pageSize - (pageSize - 1));
    setEnd(end);
  };

  const handleClick = (page: number) => {
    goToPage(page);
  };

  const handleMoveLeft = () => {
    goToPage(currentPage - 1);
  };

  const handleMoveRight = () => {
    goToPage(currentPage + 1);
  };

  const pages = fetchPageNumbers();

  return (
    <React.Fragment>
      <ul className="pagination">
        {canPreviousPage && (
          <li className="paginate_button page-item previous" style={{ cursor: "pointer" }}>
            <a className="page-link" onClick={handleMoveLeft}>
              {"<"}
            </a>
          </li>
        )}
        {pages.map((page, index) => {
          if (page === LEFT_ELLIPSIS)
            return (
              <li key={index} className="paginate_button page-item disabled">
                <a>{"..."}</a>
              </li>
            );

          if (page === RIGHT_ELLIPSIS)
            return (
              <li key={index} className="paginate_button page-item disabled">
                <a>{"..."}</a>
              </li>
            );

          return (
            <li key={index} className={`paginate_button page-item ${currentPage == page && "active"}`} style={{ cursor: "pointer" }}>
              <a className="page-link" onClick={() => handleClick(page)}>
                {page}
              </a>
            </li>
          );
        })}
        {canNextPage && (
          <li className="paginate_button page-item next" style={{ cursor: "pointer" }}>
            <a className="page-link" onClick={handleMoveRight}>
              {">"}
            </a>
          </li>
        )}
      </ul>
    </React.Fragment>
  );
};
